import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useModal } from './ModalContext';
import SearchSelect from 'components/forms/basefields/SearchSelect';
import { FieldOption } from 'types/FieldTypes';
import SecondaryButton from 'components/buttons/SecondaryButton';
import PrimaryButton from 'components/buttons/PrimaryButton';
import '../../style/scss/contact-merge.scss';

interface ContactMergeSearchModalProps {
    contactId: number;
    entityType: string | null;
    contactType: string;
    onConfirm: (selectedContactId: number) => void;
}

/*
 * ContactMergeSearchModal.tsx
 * Lets the user search the contact to merge the current contact with.
 * Only contacts of the same entity type are offered, and a supplier
 * only merges with other suppliers.
 */
const ContactMergeSearchModal: React.FC<ContactMergeSearchModalProps> = ({ contactId, entityType, contactType, onConfirm }) => {
    const { t } = useTranslation();
    const { revealModal, closeModal } = useModal();
    const [selectedContact, setSelectedContact] = useState<FieldOption | null>(null);

    // Reveal the modal on first render
    useEffect(() => { revealModal() }, []);

    // Build the search query, the same entity type and supplier scope as the current contact
    const query = useMemo(() => ({
        endpoint: 'get_contact_search',
        params: {
            entity_type: entityType,
            contact_type: contactType === 'supplier' ? ['supplier'] : ['contact', 'lead', 'client'],
            exclude_id: contactId,
        },
    }), [contactId, entityType, contactType]);

    // Close the modal and open the merge page with the selected contact
    const handleConfirm = () => {
        if (!selectedContact) return;
        closeModal();
        onConfirm(Number(selectedContact.id));
    };

    return (
        <div className='contact-merge-search-modal'>
            <p className='contact-merge-search-explanation'>{t('contact.merge.search_modal_explanation')}</p>
            <div className='formset add-edit-form'>
                <div className='formfield'>
                    <SearchSelect
                        type='searchselect'
                        name='merge_contact'
                        placeholder='contact.merge.search_placeholder'
                        objectName='contact.general.object_name.singular'
                        query={query}
                        selectionFormat='name'
                        optionFormat={{
                            title: { field: 'name', format: (value) => value },
                            subtitle: { field: 'primary_location', format: (value) => value?.address, optional: true },
                        }}
                        disableAddNew={true}
                        onSelect={(option) => setSelectedContact(option)} />
                </div>
                <div className='buttons-right'>
                    <SecondaryButton
                        onClick={() => closeModal()}
                        label='general.cancel'
                        size='small' />
                    <PrimaryButton
                        onClick={handleConfirm}
                        label='contact.merge.search_modal_button_label'
                        size='small'
                        disabled={{ disabled: !selectedContact }} />
                </div>
            </div>
        </div>
    );
};

export default ContactMergeSearchModal;
